import React, { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { API, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ArrowLeft, KeyRound, CheckCircle2 } from "lucide-react";
import CoreLogo from "@/components/CoreLogo";

export default function ResetPasswordPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const [pw, setPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (pw !== confirmPw) {
      setError("New password and confirmation do not match.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/login"), 2500);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center items-center px-6 py-12 bg-white" data-testid="reset-password-page">
      <div className="max-w-md w-full">
        <div className="mb-8 text-neutral-900" data-testid="reset-core-logo">
          <CoreLogo variant="inline" size={42} />
        </div>
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-neutral-500 mb-3">Account Recovery</div>
        <h1 className="font-serif text-4xl sm:text-5xl text-neutral-900 mb-3">Set a new password.</h1>

        {!done ? (
          <>
            <p className="text-sm text-neutral-500 mb-10">
              Choose a password you haven&apos;t used before. We recommend a four-word passphrase.
            </p>
            <form onSubmit={submit} className="space-y-5" data-testid="reset-password-form">
              <div>
                <Label htmlFor="pw" className="text-xs font-sans uppercase tracking-[0.2em] text-neutral-500">New Password</Label>
                <Input
                  id="pw"
                  type="password"
                  required
                  minLength={6}
                  autoComplete="new-password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900"
                  data-testid="reset-new-password-input"
                />
              </div>
              <div>
                <Label htmlFor="confirm" className="text-xs font-sans uppercase tracking-[0.2em] text-neutral-500">Confirm Password</Label>
                <Input
                  id="confirm"
                  type="password"
                  required
                  minLength={6}
                  autoComplete="new-password"
                  value={confirmPw}
                  onChange={(e) => setConfirmPw(e.target.value)}
                  className="mt-2 rounded-none border-neutral-300 focus-visible:ring-0 focus-visible:border-neutral-900"
                  data-testid="reset-confirm-password-input"
                />
              </div>
              {error && <p className="text-sm text-red-600" data-testid="reset-error">{error}</p>}
              <Button
                type="submit"
                disabled={submitting}
                className="w-full rounded-none bg-neutral-900 hover:bg-neutral-800 text-white h-11 font-sans tracking-wide"
                data-testid="reset-submit-button"
              >
                <KeyRound className="h-4 w-4 mr-2" strokeWidth={1.5} />
                {submitting ? "Updating…" : "Set new password"}
              </Button>
            </form>
          </>
        ) : (
          <div className="border border-neutral-200 p-6 flex items-start gap-3" data-testid="reset-password-success">
            <CheckCircle2 className="h-5 w-5 text-emerald-700 mt-0.5 shrink-0" strokeWidth={1.5} />
            <div>
              <div className="font-serif text-xl mb-2">Password updated.</div>
              <p className="text-sm text-neutral-600">Redirecting you to sign in…</p>
            </div>
          </div>
        )}

        <div className="mt-10 pt-6 border-t border-neutral-200 text-sm">
          <Link to="/login" className="inline-flex items-center gap-2 text-neutral-500 hover:text-neutral-900" data-testid="reset-back-to-login">
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
            Back to sign in
          </Link>
        </div>
      </div>
    </div>
  );
}
